/**
 * Cross-Browser Color Rendering Test Suite
 * Tests color rendering consistency across different browsers and devices
 */

class CrossBrowserColorTest {
  constructor() {
    this.testResults = {
      colorAccuracy: {},
      gradientRendering: {},
      contrastRatios: {},
      performanceMetrics: {},
      browserSpecific: {}
    };
    
    this.colorTestCases = [
      { name: 'Primary Blue', hex: '#2563EB', cssVar: '--color-primary-blue-500' },
      { name: 'Primary Purple', hex: '#7C3AED', cssVar: '--color-primary-purple-500' },
      { name: 'Primary Teal', hex: '#0891B2', cssVar: '--color-primary-teal-500' },
      { name: 'Accent Orange', hex: '#F59E0B', cssVar: '--color-accent-orange-500' },
      { name: 'Success Green', hex: '#10B981', cssVar: '--color-success-500' },
      { name: 'Error Red', hex: '#EF4444', cssVar: '--color-error-500' },
      { name: 'Background Dark', hex: '#0F172A', cssVar: '--bg-primary' },
      { name: 'Text Light', hex: '#F8FAFC', cssVar: '--text-primary' }
    ];
    
    this.gradientTestCases = [
      {
        name: 'Primary Gradient',
        css: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
        cssVar: '--gradient-primary'
      },
      {
        name: 'Secondary Gradient',
        css: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)',
        cssVar: '--gradient-secondary'
      },
      {
        name: 'Accent Gradient',
        css: 'linear-gradient(135deg, #F59E0B 0%, #EC4899 100%)',
        cssVar: '--gradient-accent'
      }
    ];
    
    this.init();
  }

  /**
   * Initialize the test suite
   */
  init() {
    this.runAllTests();
  }

  /**
   * Run all color tests
   */
  async runAllTests() {
    console.log('Starting cross-browser color tests...');
    
    await this.testColorAccuracy();
    await this.testGradientRendering();
    await this.testContrastRatios();
    await this.testPerformanceMetrics();
    await this.testBrowserSpecificFeatures();
    
    console.log('Cross-browser color tests completed:', this.testResults);
    
    return this.generateTestReport();
  }

  /**
   * Test color accuracy across different rendering contexts
   */
  async testColorAccuracy() {
    console.log('Testing color accuracy...');
    
    for (const testCase of this.colorTestCases) {
      const results = await this.testSingleColor(testCase);
      this.testResults.colorAccuracy[testCase.name] = results;
    }
  }

  /**
   * Test a single color in different contexts
   */
  async testSingleColor(testCase) {
    const testElement = document.createElement('div');
    testElement.style.position = 'absolute';
    testElement.style.top = '-9999px';
    testElement.style.width = '100px';
    testElement.style.height = '100px';
    document.body.appendChild(testElement);

    const results = {
      expectedHex: testCase.hex,
      cssVarValue: null,
      computedColor: null,
      rgbValues: null,
      colorMatch: false,
      deltaE: null
    };

    try {
      // Test CSS custom property value
      if (testCase.cssVar) {
        const cssVarValue = getComputedStyle(document.documentElement)
          .getPropertyValue(testCase.cssVar).trim();
        results.cssVarValue = cssVarValue;
      }

      // Test computed color
      testElement.style.backgroundColor = testCase.hex;
      const computedStyle = getComputedStyle(testElement);
      results.computedColor = computedStyle.backgroundColor;

      // Parse RGB values
      const rgbMatch = results.computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        results.rgbValues = {
          r: parseInt(rgbMatch[1]),
          g: parseInt(rgbMatch[2]),
          b: parseInt(rgbMatch[3])
        };

        // Calculate color difference (Delta E)
        const expectedRgb = this.hexToRgb(testCase.hex);
        if (expectedRgb) {
          results.deltaE = this.calculateDeltaE(expectedRgb, results.rgbValues);
          results.colorMatch = results.deltaE < 2.3; // JND threshold
        }
      }

      // Test with CSS custom property
      if (testCase.cssVar) {
        testElement.style.backgroundColor = `var(${testCase.cssVar})`;
        const cssVarComputedColor = getComputedStyle(testElement).backgroundColor;
        results.cssVarComputedColor = cssVarComputedColor;
        results.cssVarMatch = cssVarComputedColor === results.computedColor;
      }

    } catch (error) {
      console.warn(`Color test failed for ${testCase.name}:`, error);
      results.error = error.message;
    } finally {
      document.body.removeChild(testElement);
    }

    return results;
  }

  /**
   * Test gradient rendering consistency
   */
  async testGradientRendering() {
    console.log('Testing gradient rendering...');
    
    for (const testCase of this.gradientTestCases) {
      const results = await this.testSingleGradient(testCase);
      this.testResults.gradientRendering[testCase.name] = results;
    }
  }

  /**
   * Test a single gradient
   */
  async testSingleGradient(testCase) {
    const testElement = document.createElement('div');
    testElement.style.position = 'absolute';
    testElement.style.top = '-9999px';
    testElement.style.width = '200px';
    testElement.style.height = '100px';
    document.body.appendChild(testElement);

    const results = {
      expectedGradient: testCase.css,
      isSupported: false,
      computedBackground: null,
      fallbackColor: null,
      renderingQuality: null
    };

    try {
      // Test gradient support
      testElement.style.background = testCase.css;
      const computedStyle = getComputedStyle(testElement);
      results.computedBackground = computedStyle.background;
      results.isSupported = results.computedBackground.includes('gradient');

      // Test CSS custom property gradient
      if (testCase.cssVar) {
        testElement.style.background = `var(${testCase.cssVar})`;
        const cssVarBackground = getComputedStyle(testElement).background;
        results.cssVarBackground = cssVarBackground;
        results.cssVarSupported = cssVarBackground.includes('gradient');
      }

      // Test fallback behavior
      if (!results.isSupported) {
        // Check if a fallback color is applied
        testElement.style.backgroundColor = '#2563EB';
        testElement.style.background = testCase.css;
        const fallbackStyle = getComputedStyle(testElement);
        results.fallbackColor = fallbackStyle.backgroundColor;
      }

      // Test rendering quality by sampling colors at different points
      if (results.isSupported) {
        results.renderingQuality = await this.testGradientQuality(testElement);
      }

    } catch (error) {
      console.warn(`Gradient test failed for ${testCase.name}:`, error);
      results.error = error.message;
    } finally {
      document.body.removeChild(testElement);
    }

    return results;
  }

  /**
   * Test gradient rendering quality
   */
  async testGradientQuality(element) {
    // This would require canvas-based color sampling in a real implementation
    // For now, we'll return a basic quality assessment
    return {
      smoothness: 'good', // Would be calculated from color sampling
      colorStops: 'accurate', // Would be verified against expected values
      performance: 'acceptable' // Would be measured from rendering time
    };
  }

  /**
   * Test contrast ratios for accessibility compliance
   */
  async testContrastRatios() {
    console.log('Testing contrast ratios...');
    
    const contrastPairs = [
      { bg: '#0F172A', fg: '#F8FAFC', name: 'Primary Background/Text' },
      { bg: '#2563EB', fg: '#FFFFFF', name: 'Primary Button' },
      { bg: '#1E293B', fg: '#CBD5E1', name: 'Secondary Background/Text' },
      { bg: '#10B981', fg: '#FFFFFF', name: 'Success Button' },
      { bg: '#EF4444', fg: '#FFFFFF', name: 'Error Button' },
      { bg: '#F59E0B', fg: '#000000', name: 'Warning Button' }
    ];

    for (const pair of contrastPairs) {
      const ratio = this.calculateContrastRatio(pair.bg, pair.fg);
      const wcagAA = ratio >= 4.5;
      const wcagAAA = ratio >= 7;
      
      this.testResults.contrastRatios[pair.name] = {
        backgroundColor: pair.bg,
        textColor: pair.fg,
        contrastRatio: ratio,
        wcagAA,
        wcagAAA,
        rating: wcagAAA ? 'AAA' : wcagAA ? 'AA' : 'Fail'
      };
    }
  }

  /**
   * Test performance metrics for color operations
   */
  async testPerformanceMetrics() {
    console.log('Testing performance metrics...');
    
    const metrics = {
      colorTransitionTime: await this.measureColorTransition(),
      gradientRenderTime: await this.measureGradientRender(),
      cssVariableAccessTime: await this.measureCSSVariableAccess(),
      colorCalculationTime: await this.measureColorCalculation()
    };

    this.testResults.performanceMetrics = metrics;
  }

  /**
   * Measure color transition performance
   */
  async measureColorTransition() {
    return new Promise((resolve) => {
      const testElement = document.createElement('div');
      testElement.style.position = 'absolute';
      testElement.style.top = '-9999px';
      testElement.style.width = '100px';
      testElement.style.height = '100px';
      testElement.style.backgroundColor = '#2563EB';
      testElement.style.transition = 'background-color 0.3s ease';
      document.body.appendChild(testElement);

      const startTime = performance.now();
      
      testElement.addEventListener('transitionend', () => {
        const endTime = performance.now();
        document.body.removeChild(testElement);
        resolve(endTime - startTime);
      });

      // Trigger transition
      requestAnimationFrame(() => {
        testElement.style.backgroundColor = '#7C3AED';
      });
    });
  }

  /**
   * Measure gradient rendering performance
   */
  async measureGradientRender() {
    const testElement = document.createElement('div');
    testElement.style.position = 'absolute';
    testElement.style.top = '-9999px';
    testElement.style.width = '200px';
    testElement.style.height = '100px';
    document.body.appendChild(testElement);

    const startTime = performance.now();
    
    testElement.style.background = 'linear-gradient(135deg, #2563EB 0%, #7C3AED 50%, #4F46E5 100%)';
    
    // Force reflow to measure rendering time
    // eslint-disable-next-line no-unused-expressions
    testElement.offsetHeight;
    
    const endTime = performance.now();
    
    document.body.removeChild(testElement);
    
    return endTime - startTime;
  }

  /**
   * Measure CSS variable access performance
   */
  async measureCSSVariableAccess() {
    const testElement = document.createElement('div');
    testElement.style.position = 'absolute';
    testElement.style.top = '-9999px';
    document.body.appendChild(testElement);

    const startTime = performance.now();
    
    // Access multiple CSS variables
    const variables = [
      '--color-primary-blue-500',
      '--color-primary-purple-500',
      '--color-primary-teal-500',
      '--bg-primary',
      '--text-primary'
    ];

    variables.forEach(variable => {
      testElement.style.backgroundColor = `var(${variable})`;
      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(testElement).backgroundColor;
    });
    
    const endTime = performance.now();
    
    document.body.removeChild(testElement);
    
    return endTime - startTime;
  }

  /**
   * Measure color calculation performance
   */
  async measureColorCalculation() {
    const startTime = performance.now();
    
    // Perform various color calculations
    for (let i = 0; i < 100; i++) {
      this.hexToRgb('#2563EB');
      this.rgbToHsl(37, 99, 235);
      this.calculateContrastRatio('#2563EB', '#FFFFFF');
    }
    
    const endTime = performance.now();
    
    return endTime - startTime;
  }

  /**
   * Test browser-specific features
   */
  async testBrowserSpecificFeatures() {
    console.log('Testing browser-specific features...');
    
    const features = {
      cssCustomProperties: this.testCSSCustomProperties(),
      gradientSupport: this.testGradientSupport(),
      backdropFilter: this.testBackdropFilter(),
      colorGamut: this.testColorGamut(),
      hdr: this.testHDRSupport(),
      colorScheme: this.testColorSchemeSupport()
    };

    this.testResults.browserSpecific = features;
  }

  /**
   * Test CSS custom properties support
   */
  testCSSCustomProperties() {
    try {
      return CSS.supports('--test', 'test') || 
             CSS.supports('color', 'var(--test)');
    } catch (error) {
      return false;
    }
  }

  /**
   * Test gradient support
   */
  testGradientSupport() {
    try {
      return CSS.supports('background', 'linear-gradient(red, blue)') ||
             CSS.supports('background', '-webkit-linear-gradient(red, blue)') ||
             CSS.supports('background', '-moz-linear-gradient(red, blue)');
    } catch (error) {
      return false;
    }
  }

  /**
   * Test backdrop filter support
   */
  testBackdropFilter() {
    try {
      return CSS.supports('backdrop-filter', 'blur(10px)') ||
             CSS.supports('-webkit-backdrop-filter', 'blur(10px)');
    } catch (error) {
      return false;
    }
  }

  /**
   * Test color gamut support
   */
  testColorGamut() {
    try {
      if (window.matchMedia) {
        return {
          p3: window.matchMedia('(color-gamut: p3)').matches,
          srgb: window.matchMedia('(color-gamut: srgb)').matches,
          rec2020: window.matchMedia('(color-gamut: rec2020)').matches
        };
      }
      return { srgb: true };
    } catch (error) {
      return { srgb: true };
    }
  }

  /**
   * Test HDR support
   */
  testHDRSupport() {
    try {
      if (window.matchMedia) {
        return {
          hdr10: window.matchMedia('(dynamic-range: high)').matches,
          standardDynamicRange: window.matchMedia('(dynamic-range: standard)').matches
        };
      }
      return { standardDynamicRange: true };
    } catch (error) {
      return { standardDynamicRange: true };
    }
  }

  /**
   * Test color scheme support
   */
  testColorSchemeSupport() {
    try {
      if (window.matchMedia) {
        return {
          dark: window.matchMedia('(prefers-color-scheme: dark)').matches,
          light: window.matchMedia('(prefers-color-scheme: light)').matches,
          noPreference: window.matchMedia('(prefers-color-scheme: no-preference)').matches
        };
      }
      return { light: true };
    } catch (error) {
      return { light: true };
    }
  }

  /**
   * Calculate contrast ratio between two colors
   */
  calculateContrastRatio(color1, color2) {
    try {
      const luminance1 = this.getLuminance(color1);
      const luminance2 = this.getLuminance(color2);
      
      const lighter = Math.max(luminance1, luminance2);
      const darker = Math.min(luminance1, luminance2);
      
      return (lighter + 0.05) / (darker + 0.05);
    } catch (error) {
      return 1;
    }
  }

  /**
   * Get relative luminance of a color
   */
  getLuminance(color) {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 0;

    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  /**
   * Convert hex to RGB
   */
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Convert RGB to HSL
   */
  rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  /**
   * Calculate Delta E color difference
   */
  calculateDeltaE(color1, color2) {
    // Simplified Delta E calculation (CIE76)
    const lab1 = this.rgbToLab(color1);
    const lab2 = this.rgbToLab(color2);
    
    const deltaL = lab1.l - lab2.l;
    const deltaA = lab1.a - lab2.a;
    const deltaB = lab1.b - lab2.b;
    
    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
  }

  /**
   * Convert RGB to LAB color space
   */
  rgbToLab(rgb) {
    // Simplified RGB to LAB conversion
    // This is a basic implementation - a full implementation would use proper color space conversion
    const { r, g, b } = rgb;
    
    // Normalize RGB values
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    
    // Convert to LAB (simplified)
    const l = 0.299 * rNorm + 0.587 * gNorm + 0.114 * bNorm;
    const a = (rNorm - gNorm) * 127;
    const bLab = (gNorm - bNorm) * 127;
    
    return { l: l * 100, a, b: bLab };
  }

  /**
   * Generate comprehensive test report
   */
  generateTestReport() {
    const report = {
      timestamp: new Date().toISOString(),
      browser: navigator.userAgent,
      testResults: this.testResults,
      summary: this.generateSummary(),
      recommendations: this.generateRecommendations()
    };

    return report;
  }

  /**
   * Generate test summary
   */
  generateSummary() {
    const colorAccuracyPassed = Object.values(this.testResults.colorAccuracy)
      .filter(result => result.colorMatch).length;
    const totalColorTests = Object.keys(this.testResults.colorAccuracy).length;

    const gradientsPassed = Object.values(this.testResults.gradientRendering)
      .filter(result => result.isSupported).length;
    const totalGradientTests = Object.keys(this.testResults.gradientRendering).length;

    const contrastPassed = Object.values(this.testResults.contrastRatios)
      .filter(result => result.wcagAA).length;
    const totalContrastTests = Object.keys(this.testResults.contrastRatios).length;

    return {
      colorAccuracy: `${colorAccuracyPassed}/${totalColorTests} colors rendered accurately`,
      gradientSupport: `${gradientsPassed}/${totalGradientTests} gradients supported`,
      contrastCompliance: `${contrastPassed}/${totalContrastTests} color pairs meet WCAG AA`,
      overallScore: Math.round(
        ((colorAccuracyPassed / totalColorTests) +
         (gradientsPassed / totalGradientTests) +
         (contrastPassed / totalContrastTests)) / 3 * 100
      )
    };
  }

  /**
   * Generate recommendations based on test results
   */
  generateRecommendations() {
    const recommendations = [];

    // Check color accuracy
    const inaccurateColors = Object.entries(this.testResults.colorAccuracy)
      .filter(([, result]) => !result.colorMatch);
    
    if (inaccurateColors.length > 0) {
      recommendations.push({
        type: 'color-accuracy',
        severity: 'medium',
        message: `${inaccurateColors.length} colors are not rendering accurately`,
        action: 'Consider using fallback colors or adjusting color values'
      });
    }

    // Check gradient support
    const unsupportedGradients = Object.entries(this.testResults.gradientRendering)
      .filter(([, result]) => !result.isSupported);
    
    if (unsupportedGradients.length > 0) {
      recommendations.push({
        type: 'gradient-support',
        severity: 'high',
        message: `${unsupportedGradients.length} gradients are not supported`,
        action: 'Implement fallback solid colors for unsupported gradients'
      });
    }

    // Check contrast compliance
    const failedContrast = Object.entries(this.testResults.contrastRatios)
      .filter(([, result]) => !result.wcagAA);
    
    if (failedContrast.length > 0) {
      recommendations.push({
        type: 'contrast-compliance',
        severity: 'high',
        message: `${failedContrast.length} color combinations fail WCAG AA standards`,
        action: 'Adjust colors to meet minimum contrast requirements'
      });
    }

    // Check performance
    const slowOperations = Object.entries(this.testResults.performanceMetrics)
      .filter(([, time]) => time > 50); // 50ms threshold
    
    if (slowOperations.length > 0) {
      recommendations.push({
        type: 'performance',
        severity: 'medium',
        message: `${slowOperations.length} color operations are slow`,
        action: 'Optimize color calculations and transitions'
      });
    }

    return recommendations;
  }

  /**
   * Export test results
   */
  exportResults(format = 'json') {
    const report = this.generateTestReport();
    
    if (format === 'json') {
      return JSON.stringify(report, null, 2);
    } else if (format === 'csv') {
      return this.convertToCSV(report);
    }
    
    return report;
  }

  /**
   * Convert results to CSV format
   */
  convertToCSV(report) {
    const rows = [];
    
    // Add headers
    rows.push(['Test Type', 'Test Name', 'Result', 'Details']);
    
    // Add color accuracy results
    Object.entries(report.testResults.colorAccuracy).forEach(([name, result]) => {
      rows.push([
        'Color Accuracy',
        name,
        result.colorMatch ? 'Pass' : 'Fail',
        `Delta E: ${result.deltaE || 'N/A'}`
      ]);
    });
    
    // Add gradient results
    Object.entries(report.testResults.gradientRendering).forEach(([name, result]) => {
      rows.push([
        'Gradient Support',
        name,
        result.isSupported ? 'Pass' : 'Fail',
        result.error || 'N/A'
      ]);
    });
    
    // Add contrast results
    Object.entries(report.testResults.contrastRatios).forEach(([name, result]) => {
      rows.push([
        'Contrast Ratio',
        name,
        result.wcagAA ? 'Pass' : 'Fail',
        `Ratio: ${result.contrastRatio.toFixed(2)}`
      ]);
    });
    
    return rows.map(row => row.join(',')).join('\n');
  }
}

// Export for use in other modules
export default CrossBrowserColorTest;

// Auto-run tests in development
if (process.env.NODE_ENV === 'development') {
  const colorTest = new CrossBrowserColorTest();
  
  // Log results after tests complete
  setTimeout(() => {
    console.log('Cross-browser color test results:', colorTest.exportResults());
  }, 1000);
}